import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import { getEventTeamAccess, setEventTeamAccess } from '@/lib/pro-team-api';

export default function EventTeamAccessCard({ eventId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [proActive, setProActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErrorCode(null);
    try {
      const res = await getEventTeamAccess(eventId);
      setRows(res.team_access || []);
      setProActive(res.pro_subscription_active);
    } catch (e) { setErrorCode(e?.code || 'ERROR'); }
    finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  async function onToggle(row, next) {
    setBusy(row.member_user_id);
    try {
      const res = await setEventTeamAccess(eventId, row.member_user_id, next);
      setRows((prev) => prev.map((r) => r.member_user_id === row.member_user_id ? { ...r, enabled: res.enabled } : r));
    } catch (e) { window.alert(e?.message || 'Could not update access.'); }
    finally { setBusy(null); }
  }

  // Non-pro or non-host: silently render nothing
  if (errorCode === 'PROFESSIONAL_ONLY' || errorCode === 'FORBIDDEN') return null;

  return (
    <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="event-team-access-card">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-4 h-4 text-[#0B1B46]" />
        <span className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">Team access</span>
      </div>
      <p className="text-xs text-[#5B6472] leading-relaxed mb-3">
        Toggle which teammates get co-host access to this event. They&apos;ll be able to review uploads,
        publish the lookbook, and manage guests — but only on the events you enable.
      </p>

      {loading ? (
        <div className="flex justify-center py-4" data-testid="event-team-access-loading"><div className="w-5 h-5 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div data-testid="event-team-access-empty">
          <p className="text-xs text-[#5B6472] mb-1.5">No teammates yet.</p>
          <button onClick={() => navigate('/pro-team')} className="text-xs font-bold text-[#146BFF] hover:underline" data-testid="event-team-access-manage-btn">
            Invite teammates &rarr;
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-col divide-y divide-[#E3E8EF]">
            {rows.map((r) => (
              <div key={r.member_user_id} className="flex items-center gap-3 py-2.5" data-testid={`event-team-access-row-${r.member_user_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#0B1B46] truncate">
                    {(r.member_first_name || r.member_last_name)
                      ? `${r.member_first_name || ''} ${r.member_last_name || ''}`.trim()
                      : (r.member_email || 'Teammate')}
                  </p>
                  {r.member_email && <p className="text-[11px] text-[#5B6472] truncate mt-0.5">{r.member_email}</p>}
                </div>
                <button
                  onClick={() => onToggle(r, !r.enabled)}
                  disabled={busy === r.member_user_id || (!proActive && !r.enabled)}
                  className={`w-9 h-5 rounded-full relative transition-colors ${r.enabled ? 'bg-[#146BFF]' : 'bg-[#D1D5DB]'} disabled:opacity-50`}
                  role="switch" aria-checked={r.enabled}
                  data-testid={`event-team-access-toggle-${r.member_user_id}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${r.enabled ? 'left-[18px]' : 'left-0.5'}`} />
                </button>
              </div>
            ))}
          </div>
          {!proActive && (
            <p className="text-[11px] font-semibold text-[#B86500] mt-2" data-testid="event-team-access-lapsed-hint">
              PRO paused — existing access stays live but you can&apos;t grant new access until you renew.
            </p>
          )}
        </>
      )}
    </div>
  );
}
